import React, { FC, useEffect, useMemo, useState } from "react";
import TokenService from "services/token.service";
import {
  fetchInboxAttachmentBlob,
  formatInboxAttachmentSize,
  InboxMessageAttachment,
  isImageInboxAttachment,
  isProcessingInboxAttachment,
} from "./inboxMedia";

interface InboxMessageAttachmentsProps {
  attachments: InboxMessageAttachment[];
  baseUrl: string;
  messageId: string;
  outbound: boolean;
}

interface AttachmentCardProps {
  attachment: InboxMessageAttachment;
  baseUrl: string;
  messageId: string;
  outbound: boolean;
}

function attachmentUnavailable(status: string): boolean {
  return ["failed", "rejected", "missing"].includes(status.toLowerCase());
}

const AttachmentCard: FC<AttachmentCardProps> = ({
  attachment,
  baseUrl,
  messageId,
  outbound,
}) => {
  const { id, filename, contentType, size, sha256, status, downloadUrl } =
    attachment;
  const requestAttachment = useMemo(
    () => ({
      id,
      filename,
      contentType,
      size,
      sha256,
      status,
      ...(downloadUrl ? { downloadUrl } : {}),
    }),
    [contentType, downloadUrl, filename, id, sha256, size, status]
  );
  const image = isImageInboxAttachment(requestAttachment);
  const unavailable = attachmentUnavailable(status);
  const processing = isProcessingInboxAttachment(requestAttachment);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(image && !unavailable && !processing);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!image || unavailable || processing) return;
    const controller = new AbortController();
    let createdUrl: string | null = null;
    let cancelled = false;
    setLoading(true);
    setError(null);

    void fetchInboxAttachmentBlob({
      baseUrl,
      token: TokenService.getLocalAccessToken(),
      messageId,
      attachment: requestAttachment,
      signal: controller.signal,
    })
      .then((blob) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setObjectUrl(createdUrl);
        setError(null);
      })
      .catch((caught: any) => {
        if (!cancelled && caught?.name !== "AbortError") {
          setError(caught?.message || "Image preview unavailable.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [baseUrl, image, messageId, processing, requestAttachment, unavailable]);

  const download = async () => {
    if (unavailable || processing || downloading) return;
    setDownloading(true);
    setError(null);
    try {
      const blob = await fetchInboxAttachmentBlob({
        baseUrl,
        token: TokenService.getLocalAccessToken(),
        messageId,
        attachment: requestAttachment,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (caught: any) {
      setError(caught?.message || "Download failed.");
    } finally {
      setDownloading(false);
    }
  };

  const meta = [contentType, formatInboxAttachmentSize(size)]
    .filter(Boolean)
    .join(" · ");
  const cardClass = outbound
    ? "border-white/25 bg-white/10 text-white hover:bg-white/15"
    : "border-themed-border bg-themed-background text-themed-text hover:bg-themed-hover";
  const mutedClass = outbound ? "text-white/70" : "text-themed-textSecondary";

  if (image && objectUrl) {
    return (
      <a
        href={objectUrl}
        download={filename}
        className={`block overflow-hidden rounded-xl border ${cardClass}`}
        title={`Download ${filename}`}
      >
        <img
          src={objectUrl}
          alt={filename}
          className="block max-h-[320px] max-w-full object-contain"
        />
        <span className="flex items-center justify-between gap-3 px-3 py-2 text-[11px]">
          <span className="min-w-0 truncate font-medium">{filename}</span>
          <span className={`shrink-0 ${mutedClass}`}>
            {formatInboxAttachmentSize(size)}
          </span>
        </span>
      </a>
    );
  }

  return (
    <button
      type="button"
      disabled={unavailable || processing || downloading || loading}
      onClick={download}
      className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${cardClass}`}
      title={
        unavailable
          ? `${filename} is unavailable`
          : processing
          ? `${filename} is processing`
          : `Download ${filename}`
      }
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
          outbound ? "bg-white/15" : "bg-[#6366F1]/10 text-[#6366F1]"
        }`}
        aria-hidden="true"
      >
        {image ? (
          <svg
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
          >
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <circle cx="8.5" cy="9" r="1.5" />
            <path d="m4 17 5-5 4 4 2-2 5 4" />
          </svg>
        ) : (
          <svg
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
          >
            <path d="M7 3h7l4 4v14H7z" />
            <path d="M14 3v5h5M10 13h5M10 17h5" />
          </svg>
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12px] font-semibold">
          {filename}
        </span>
        <span className={`block truncate text-[10px] ${mutedClass}`}>
          {unavailable
            ? `Unavailable (${status})`
            : processing
            ? "Processing attachment…"
            : loading
            ? "Loading preview…"
            : downloading
            ? "Downloading…"
            : error || meta || "Download attachment"}
        </span>
      </span>
      {!unavailable && !processing && !loading && (
        <svg
          className="h-4 w-4 shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path d="M12 3v12m0 0 4-4m-4 4-4-4M5 20h14" />
        </svg>
      )}
    </button>
  );
};

const InboxMessageAttachments: FC<InboxMessageAttachmentsProps> = ({
  attachments,
  baseUrl,
  messageId,
  outbound,
}) => {
  if (attachments.length === 0) return null;
  return (
    <div className="mt-2 space-y-2">
      {attachments.map((attachment) => (
        <AttachmentCard
          key={attachment.id}
          attachment={attachment}
          baseUrl={baseUrl}
          messageId={messageId}
          outbound={outbound}
        />
      ))}
    </div>
  );
};

export default InboxMessageAttachments;
